/**
 * The Complete step (`/account/bookings/:bookingId/complete`, web-account-pages D11; web-studio-sites
 * D12) on a custom site: a booking the studio made that waits on the client, one form at a time,
 * drawn as Dana's confirmed telify-pages.html draws it. The step's rules are the shared
 * `useCompleteStep` flow's; the standard website keeps the app's column.
 */
import { definePiece } from '../../contract/define';

export const contract = definePiece({
  name: 'complete-step',
  description: "A booking the studio made that waits on the client: the booking's forms one at a time, then on to the payment or the booked page.",
  drawing: 'standard-only',
  options: {},
  parts: {
    page: { description: 'The whole step.', required: false },
    title: { description: "The booking's offering.", required: true, role: 'title' },
    step: { description: "Which form this is: Form 1 of 2 · its name, or the registration's kind line.", required: true },
    description: { description: "The form's description, when the studio wrote one.", required: false },
    finishBy: { description: 'Finish by: when the booking stops being held.', required: true },
    fields: { description: "The form's fields.", required: true },
    consent: { description: 'The privacy policy agreement box, on a first-time client’s registration.', required: true },
    policyLink: { description: 'The privacy policy, opened in a new tab.', required: true },
    consentError: { description: 'The line asking the client to agree to the privacy policy first.', required: true },
    continue: { description: 'Continue, Continue to payment or Complete Registration.', required: true, states: ['disabled'], role: 'primary-action' },
    failed: { description: 'The step could not be read.', required: true },
    retry: { description: 'Try again, after the step could not be read.', required: true, role: 'primary-action' },
    back: { description: 'Back to booking, after the step could not be read.', required: true, role: 'secondary-action' },
  },
  actions: {
    submit: { description: "Sends the client's answers to this form.", args: [{ name: 'responses', kind: 'answers' }] },
    setConsent: { description: 'Ticks or clears the privacy policy agreement.', args: [{ name: 'agreed', kind: 'value', type: 'boolean' }] },
    retry: { description: 'Reads the step again.', args: [] },
    back: { description: "Opens the booking's page, or where the client came from.", args: [] },
  },
  events: {},
});

/** What the Complete step knows. */
export interface CompleteStepState {
  /** loading until the step is read; form while it is filled in; error when it could not be read. */
  readonly status: 'loading' | 'form' | 'submitting' | 'error';
  /** The booking's offering, or the form's name until the booking is read. */
  readonly title: string | null;
  /** Form 1 of 2 · its name, or the registration's kind line. */
  readonly step: string | null;
  /** The form's description, when the studio wrote one. */
  readonly description: string | null;
  /** When the booking stops being held: Sat, Oct 31 at 6:00 PM. */
  readonly finishBy: string | null;
  /** True on the booking's last form. */
  readonly lastForm: boolean;
  /** True when the payment follows the forms directly. */
  readonly paymentAhead: boolean;
  /** True on a first-time client's registration, which needs the privacy policy agreement. */
  readonly consentRequired: boolean;
  readonly consented: boolean;
  /** True after Continue without the agreement. */
  readonly consentError: boolean;
}

/** What a drawing can ask the Complete step to do. */
export interface CompleteStepActions {
  /** Send the answers (the fields' submit calls it with their responses). */
  readonly submit: (responses: readonly { readonly fieldId: string; readonly value: unknown }[]) => void;
  /** Tick (true) or clear (false) the privacy policy agreement. */
  readonly setConsent: (agreed: boolean) => void;
  /** Read the step again after it could not be read. */
  readonly retry: () => void;
  /** Open the booking's page, or go back where the client came from. */
  readonly back: () => void;
}

/** The Complete step tells its host nothing: where it goes next is its own move. */
export type CompleteStepEvents = Record<string, never>;
